import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import {
  type GrantOpportunityDocumentRole,
  uploadGrantOpportunityDocument,
} from "@/api/grant_opportunity_documents";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type GrantDocumentUploadDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  municipalityId: number;
  opportunityId: number;
  roleOptions: Array<{ value: string; label: string }>;
  onUploaded: () => void;
};

export function GrantDocumentUploadDialog({
  open,
  onOpenChange,
  municipalityId,
  opportunityId,
  roleOptions,
  onUploaded,
}: GrantDocumentUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState<string>("attachment");
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setName("");
    setRole("attachment");
    setError(null);
  };

  const uploadMutation = useMutation({
    mutationFn: () =>
      uploadGrantOpportunityDocument(municipalityId, opportunityId, {
        file: file as File,
        name: name.trim() || undefined,
        role: role as GrantOpportunityDocumentRole,
      }),
    onSuccess: () => {
      reset();
      onOpenChange(false);
      onUploaded();
    },
    onError: (uploadError: Error) => setError(uploadError.message),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload file</DialogTitle>
          <DialogDescription>
            The file is saved to this grant's folder in File Center and linked here. You can move it
            elsewhere in File Center later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="grant-upload-file">File</Label>
            <Input
              id="grant-upload-file"
              type="file"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setError(null);
              }}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="grant-upload-name">Display name (optional)</Label>
            <Input
              id="grant-upload-name"
              value={name}
              placeholder={file?.name ?? "Defaults to the file name"}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="grant-upload-role">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger id="grant-upload-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => uploadMutation.mutate()}
            disabled={!file || uploadMutation.isPending}
          >
            {uploadMutation.isPending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
            ) : null}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
